/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import React from "react";

import { AdminSalesDashboardSummarySkeleton } from "./AdminSalesDashboardSummarySkeleton";

type AdminSalesDashboardSummaryCard = {
  label: string;
  value: string;
  prefix?: string;
  unit?: string;
};

export const AdminSalesDashboardSummary: React.FC<{
  error: unknown;
  isSummaryLoading: boolean;
  summaryCards: AdminSalesDashboardSummaryCard[] | undefined;
}> = ({ error, isSummaryLoading, summaryCards }) => {
  if (error) {
    return (
      <AdminSalesDashboardSummaryState
        hasError={true}
        message="サマリーデータの取得に失敗しました"
      />
    );
  }

  if (!summaryCards && isSummaryLoading) {
    return <AdminSalesDashboardSummarySkeleton />;
  }

  if (!summaryCards) {
    return (
      <AdminSalesDashboardSummaryState
        hasError={false}
        message="データがありません"
      />
    );
  }

  return (
    <div css={styles.cardGrid}>
      {summaryCards.map((card, index) => {
        return (
          <article css={styles.card} key={`${card.label}_${index}`}>
            <span css={styles.label}>{card.label}</span>
            <div css={styles.valueArea}>
              {card.prefix ? (
                <span css={styles.valuePrefix}>{card.prefix}</span>
              ) : null}
              <span css={styles.value}>{card.value}</span>
              {card.unit ? <span css={styles.unit}>{card.unit}</span> : null}
            </div>
          </article>
        );
      })}
    </div>
  );
};

const AdminSalesDashboardSummaryState: React.FC<{
  hasError: boolean;
  message: string;
}> = ({ hasError, message }) => (
  <div css={styles.statePanel} role={hasError ? "alert" : undefined}>
    {message}
  </div>
);

const styles = {
  statePanel: css`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 112px;
    padding: 24px;
    color: #b9c3cd;
    font-size: 14px;
    text-align: center;
    background: #1b2734;
    border: 1px solid #2e3d4c;
    border-radius: 10px;
    box-shadow: 0 1px 2px rgb(0 0 0 / 22%);
  `,
  cardGrid: css`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 250px), 1fr));
    gap: 14px;
  `,
  card: css`
    container-type: inline-size;
    display: flex;
    flex-direction: column;
    padding: clamp(16px, 2.5vw, 24px);
    background: #1b2734;
    border: 1px solid #2e3d4c;
    border-radius: 10px;
    box-shadow: 0 1px 2px rgb(0 0 0 / 22%);
  `,
  label: css`
    color: #8b96a2;
    font-size: 13px;
    letter-spacing: 0.4px;
  `,
  valueArea: css`
    display: flex;
    align-items: baseline;
    gap: 3px;
    min-width: 0;
    margin-top: 10px;
    font-variant-numeric: tabular-nums;
  `,
  valuePrefix: css`
    color: #b9c3cd;
    font-size: 20px;
    font-weight: 700;
  `,
  value: css`
    min-width: 0;
    color: #ffffff;
    font-size: clamp(26px, 3.2vw, 34px);
    font-weight: 800;
    letter-spacing: -0.5px;
    line-height: 1;
    white-space: nowrap;
    @supports (container-type: inline-size) {
      font-size: clamp(26px, 13cqw, 34px);
    }
  `,
  unit: css`
    margin-left: 4px;
    color: #8b96a2;
    font-size: 13px;
  `,
};
